import Trigger from "./Trigger"

import type AGL from "./AGL"
import type Epic from "./Epic"
import type FHIR from "./FHIR"
import type PWA from "./PWA"
import type Util from "./Util"

export default class SCH {
   // Static Properties
   private static version = "3.0"
   private static instance: SCH & SCHSelector
   private static script: HTMLScriptElement
   private static nonce: string

   // Private Instance Properties
   private mixinMap: { [key: string]: () => Promise<any> } = {
      'AGL': () => import('./AGL'),
      'DOMSelection': () => import('./DOMSelection'),
      'Epic': () => import('./Epic'),
      'FHIR': () => import('./FHIR'),
      'PWA': () => import('./PWA'),
      'Session': () => import('./Session'),
      'Util': () => import('./Util'),
      'behaviors': () => import('../behaviors'),
   };
   private mixins: string[] = []

   // Public Instance Mixins
   public agl?: AGL
   public epic?: Epic
   public fhir?: FHIR
   public pwa?: PWA
   public util?: Util

   // Public Instance Properties
   patID?: string | null
   patDAT?: string | null
   usrID?: string | null
   clientID?: string | null
   login?: string | null
   application?: string | null


   constructor() {
      if (!SCH.instance) {
         SCH.script = document.currentScript! as HTMLScriptElement
         SCH.nonce = SCH.script.nonce!
         this.#init()
      }
   }

   public static get Instance() {
      if (!SCH.instance) {
         const instanceFunction: SCHSelector = (...args: any[]): SCH & SCHSelector & SCHSelection => new SCHSelection(...(args as any[])) as SCH & SCHSelector & SCHSelection

         SCH.instance = Object.assign(instanceFunction, new SCH()) as SCH & SCHSelector
         Object.setPrototypeOf(SCH.instance, SCH.prototype)
      }

      return SCH.instance
   }

   public get version() {
      return SCH.version
   }

   public get nonce() {
      return SCH.nonce
   }

   public get script() {
      return SCH.script
   }

   public async config(opt: any): Promise<void> {
      if (typeof (opt) == "string") opt = { env: opt ? opt.toLowerCase() : null }
      if (opt.hasOwnProperty("mixins")) opt.mixins = [...new Set([...opt.mixins, ...this.mixins])]
      const
         env = opt.env ? opt.env : null,
         serviceRoot = opt.serviceRoot ? opt.serviceRoot : ""
      if (env) delete opt.env
      if (serviceRoot) delete opt.serviceRoot

      const
         qs = new URLSearchParams(location.search),
         scr = SCH.script,
         newOpt = Object.assign({
            patID: scr.getAttribute("data-pid") ? scr.getAttribute("data-pid")?.replace(/\+/g, " ") : qs.get("patID"),
            patDAT: scr.getAttribute("data-dat") || qs.get("patDAT"),
            usrID: opt.usrID || qs.get("userID") || scr.getAttribute("data-uid") || null,
            clientID: scr.getAttribute("data-cid") || null,
            login: document.cookie.split('; ').find(row => row.startsWith('currentUser='))?.split('=')[1] || null,
            application: this.application || null,
            mixins: []
         }, opt)

      console.hasOwnProperty('group') && console.groupCollapsed("SCH Library Config")
      for (let key in newOpt) {
         newOpt[key] && newOpt[key] != (this as any)[key] && key != "expireSession"
            && console.log(`Setting sch.${key} to "${newOpt[key]}"`);
         (this as any)[key] = newOpt[key]
      }
      console.hasOwnProperty('group') && console.groupEnd()

      await this.loadMixins(...newOpt.mixins).then(() => {
         if (this.epic) {
            this.epic.env = env
            this.epic.serviceRoot = serviceRoot
         }
      })
   }

   public error(msg: string | Blob, data: any, label: string): this {
      var out = msg + (label ? "\n\n" + label : "")
      if (this.epic && this.epic.env != "prd") {
         alert(out)
         return this
      }

      var detail: any = {
         "Browser": navigator.userAgent,
         "URL": location.href
      }

      if (this.epic && this.epic.env) detail["Environment"] = this.epic!.env
      if (this.usrID) detail["User"] = this.usrID
      data && Object.assign(detail, data)

      const
         sid = document.cookie.split('; ').find(row => row.startsWith('sid='))?.split('=')[1] || '',
         body = new FormData()

      if (this.application) detail.App = this.application

      body.append("do", "ThrowError")
      body.append("sid", sid)
      body.append("details", JSON.stringify(detail))

      fetch("./", {
         method: "post",
         body: body
      })

      return this
   }

   public async loadMixins(...mixins: string[]): Promise<void> {
      if (mixins.length) this.mixins = [...new Set([...this.mixins, ...mixins])]
      else return
      for (let ext of this.mixins) {
         let { default: mod } = await this.mixinMap[ext]()
         if (mod) {
            if (mod.prototype) {
               console.log(`Loading mixin: ${ext}`)
               const modInstance = new mod(this)
               modInstance instanceof SCHSelection
                  ? Object.getOwnPropertyNames(mod.prototype).forEach(name => (SCHSelection.prototype as any)[name] = mod.prototype[name])
                  : (this as any)[ext.toLowerCase()] = modInstance
            }
         }
      }
   }

   #init(): this {
      console.log(
         `%cInitializing SCH JavaScript Library ${this.version}...`,
         "font-weight:900;font-family:verdana"
      )

      new Trigger('error', event => {
         const { message, filename, lineno, colno, error } = event

         if (!error) {
            console.debug("Ignored non-error event:", { message, filename, lineno, colno })
            return true
         }

         this.error("JavaScript error", {
            URL: filename,
            Line: lineno,
            Column: colno,
            Error: message,
            Stack: error.stack
         }, message)
      })

      new Trigger('unhandledrejection', event => {
         const
            { reason } = event,
            rejectionMessage = (reason instanceof Error) ? reason.message : String(reason)

         this.error("Unhandled promise rejection", {
            Error: rejectionMessage,
            Stack: reason instanceof Error ? reason.stack : null
         }, rejectionMessage)
      })


      if (SCH.script.getAttribute("data-config")) {
         const conf = JSON.parse(SCH.script.getAttribute("data-config")!)
         if (conf.devEnv) {
            conf.env = location.hostname.indexOf(conf.devEnv) < 0 ? "prd" : "dev"
            delete conf.devEnv
         }
         this.config(conf)
      }
      return this
   }

}

export class SCHSelection extends SCH {
   private elements: HTMLElement[] = []

   constructor(...args: any[]) {
      super()
      const selector = args[0]
      if (typeof selector === 'string') this.elements = Array.from(document.querySelectorAll(selector))
      else if (selector instanceof HTMLElement) this.elements.push(selector)
   }

   get(i: number) {
      return i >= 0 && i < this.elements.length ? this.elements[i] : null;
   }

   get length() {
      return this.elements.length || 0
   }

   [Symbol.iterator]() {
      let index = 0
      const elements = this.elements

      return {
         next(): IteratorResult<HTMLElement> {
            if (index < elements.length) return { value: elements[index++], done: false }
            else return { value: null, done: true }
         }
      }
   }
}
